import { Link } from "react-router-dom";
import { whatsappLink } from "@/config/company";
import {
  ArrowRight,
  MessageCircle,
  Package,
  Container,
  Boxes,
  Truck,
  PackageOpen,
  Globe,
  Luggage,
  Zap,
  Car,
  MapPin,
} from "lucide-react";
import SendtooLogo from "@/components/shared/SendtooLogo"; // single logo import — swap this one line to change the logo source
import "./HeroOrbit.css";

const WHATSAPP_URL = whatsappLink("Hi Sendtoo, I need a shipping quote");

const ORBIT_SERVICES = [
  { id: "courier", label: "Courier", icon: Package },
  { id: "cargo", label: "Cargo", icon: Container },
  { id: "packers-movers", label: "Packers & Movers", icon: Boxes },
  { id: "full-truck-load", label: "Full Truck Load", icon: Truck },
  { id: "part-truck-load", label: "Part Truck Load", icon: PackageOpen },
  { id: "international", label: "International", icon: Globe },
  { id: "airport-baggage", label: "Airport Baggage", icon: Luggage },
  { id: "hyperlocal", label: "Hyperlocal", icon: Zap },
  { id: "car-bike-transport", label: "Car/Bike Transport", icon: Car },
];

const FEATURES = [
  { icon: Package, title: "Doorstep pickup", line: "From individuals to business shipments" },
  { icon: MapPin, title: "Own network in South India", line: "Cities, towns and remote mandals" },
  { icon: Truck, title: "Pan-India reach", line: "Through our national 3PL partners" },
  { icon: Globe, title: "International", line: "Courier and freight support" },
];

/**
 * Self-contained Sendtoo hero: navy dot-grid, blue glow, headline, CTA buttons,
 * service orbit and the slim feature strip underneath.
 * All styles live in HeroOrbit.css under the .so- prefix.
 */
export const HeroOrbit = () => (
  <div className="so-root">
    <section className="so-hero" data-testid="hero">
      <div className="so-hero-inner">
        <div className="so-copy">
          <span className="so-eyebrow">Your trusted logistics partner</span>
          <h1 className="so-headline font-heading" data-testid="hero-heading">
            When it has to reach,
            <br />
            <span className="so-headline-blue">we make it happen.</span>
          </h1>
          <p className="so-subline">From metro to mandal, and across India.</p>
          <p className="so-supporting">Doorstep pickup, dependable routes and international reach.</p>
          <div className="so-cta-row">
            <Link to="/auth/login" className="so-btn so-btn-white" data-testid="hero-book-shipment-btn">
              Book a shipment <ArrowRight size={18} aria-hidden="true" />
            </Link>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="so-btn so-btn-green"
              data-testid="hero-whatsapp-btn"
            >
              <MessageCircle size={18} aria-hidden="true" /> Chat on WhatsApp
            </a>
          </div>
          <div className="so-points" data-testid="hero-feature-row">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="so-point">
                  <p className="so-point-title">
                    <Icon size={16} aria-hidden="true" className="so-point-icon" />
                    {f.title}
                  </p>
                  <p className="so-point-line">{f.line}</p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="so-orbit-wrap" data-testid="hero-orbit-wrap">
          <div className="so-orbit" data-testid="service-orbit">
            <div className="so-orbit-guide" aria-hidden="true" />
            <div className="so-ring">
              {ORBIT_SERVICES.map((service, i) => {
                const Icon = service.icon;
                return (
                  <div
                    key={service.id}
                    className="so-node"
                    style={{ "--so-angle": `${i * 40}deg` }}
                    data-testid={`orbit-node-${service.id}`}
                  >
                    <span className="so-node-spin">
                      <span className="so-node-scale">
                        <span className="so-node-icon">
                          <Icon aria-hidden="true" />
                        </span>
                        <span className="so-node-label">{service.label}</span>
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="so-hub" data-testid="orbit-hub">
              <SendtooLogo size="xl" variant="light" />
            </div>
          </div>
        </div>
      </div>
    </section>

  </div>
);

export default HeroOrbit;
